import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type { SettingsSection } from "features/integrationsBeta/readinessUi";
import { setIntegrationsSettingsUnsaved } from "features/integrationsBeta/integrationsSettingsUnsaved";
import { deepEqual } from "./pendingFormat";

/**
 * Pending-changes store for the integration Settings tab: field edits are
 * STAGED here instead of firing mutations, then applied together from the
 * save bar after a review (section, field, old -> new). Structural
 * operations (create/delete/rotate/secrets/link-unlink) stay immediate.
 *
 * Lives at SettingsPanel level so records survive sub-tab switches; dies
 * with the panel (leave guards prevent accidental unmounts while dirty).
 */

export type PendingEntityType =
  | "integration"
  | "configProfile"
  | "connection"
  | "step"
  | "webhookTrigger"
  | "schedule";

export type PendingValue = string | boolean | Record<string, unknown>;

/** Drives old/new formatting in the review dialog. */
export type PendingValueKind =
  | "enabled" // boolean -> Enabled / Disabled (integration + step)
  | "active" // boolean -> Active / Inactive (webhook + schedule)
  | "text" // string -> quoted
  | "cron" // string -> summarizeAwsCron()
  | "config"; // object -> per-top-level-key diff

export interface PendingChange {
  key: string; // `${entityType}:${entityId}:${field}`
  entityType: PendingEntityType;
  entityId: number;
  section: SettingsSection;
  sectionLabel: string;
  entityLabel: string; // e.g. `Step "Apply to PDQ"`; "Integration" for integration-level
  field: string; // API field name, e.g. "is_active"
  fieldLabel: string;
  valueKind: PendingValueKind;
  oldValue: PendingValue; // server value captured at staging time
  newValue: PendingValue;
}

export type StageInput = Omit<PendingChange, "key" | "sectionLabel">;

export const SECTION_LABELS: Record<SettingsSection, string> = {
  status: "Status",
  connections: "Connections",
  steps: "Pipeline Steps",
  integration_config: "Integration Config",
  execution: "Execution",
  webhooks: "Webhooks",
  schedules: "Schedulers",
};

export const pendingKey = (
  entityType: PendingEntityType,
  entityId: number,
  field: string,
) => `${entityType}:${entityId}:${field}`;

type State = Record<string, PendingChange>;
type Action =
  | { type: "stage"; change: PendingChange }
  | { type: "removeKeys"; keys: string[] }
  | { type: "clear" };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "stage": {
      const { change } = action;
      // newValue back at oldValue -> the record disappears (toggle-back).
      if (deepEqual(change.newValue, change.oldValue)) {
        if (!(change.key in state)) return state;
        const next = { ...state };
        delete next[change.key];
        return next;
      }
      // Re-staging replaces the record; oldValue is the caller's CURRENT
      // server value, which self-heals a stale baseline.
      return { ...state, [change.key]: change };
    }
    case "removeKeys": {
      if (!action.keys.some((k) => k in state)) return state;
      const next = { ...state };
      action.keys.forEach((k) => delete next[k]);
      return next;
    }
    case "clear":
      return {};
    default:
      return state;
  }
}

export interface PendingChangesApi {
  changes: PendingChange[]; // insertion-ordered
  count: number;
  isDirty: boolean;
  stage: (input: StageInput) => void;
  getPending: (
    entityType: PendingEntityType,
    entityId: number,
    field: string,
  ) => PendingChange | undefined;
  removeKeys: (keys: string[]) => void;
  clear: () => void;
}

const Ctx = createContext<PendingChangesApi | null>(null);

export function PendingChangesProvider({
  onDirtyChange,
  children,
}: {
  onDirtyChange?: (dirty: boolean) => void;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, {});
  const count = Object.keys(state).length;

  // Stable ref so a non-memoized callback prop cannot churn the effects.
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;

  // Lift dirty to the page + module-level flag for the Header nav guard.
  useEffect(() => {
    onDirtyRef.current?.(count > 0);
    setIntegrationsSettingsUnsaved(count > 0);
  }, [count]);
  useEffect(
    () => () => {
      // Dies with the panel (sftp pattern): discarding = unmounting.
      onDirtyRef.current?.(false);
      setIntegrationsSettingsUnsaved(false);
    },
    [],
  );

  // Browser refresh/close guard.
  const dirty = count > 0;
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const api = useMemo<PendingChangesApi>(
    () => ({
      changes: Object.values(state),
      count,
      isDirty: count > 0,
      stage: (input) =>
        dispatch({
          type: "stage",
          change: {
            ...input,
            key: pendingKey(input.entityType, input.entityId, input.field),
            sectionLabel: SECTION_LABELS[input.section],
          },
        }),
      getPending: (entityType, entityId, field) =>
        state[pendingKey(entityType, entityId, field)],
      removeKeys: (keys) => dispatch({ type: "removeKeys", keys }),
      clear: () => dispatch({ type: "clear" }),
    }),
    [state, count],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function usePendingChanges(): PendingChangesApi {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error(
      "usePendingChanges must be used inside PendingChangesProvider",
    );
  }
  return ctx;
}

/**
 * Overlay helper for singleton controls. NOT usable inside .map loops -
 * call usePendingChanges().getPending there instead.
 */
export function usePendingValue<T extends PendingValue>(
  entityType: PendingEntityType,
  entityId: number,
  field: string,
  serverValue: T,
): [T, boolean] {
  const { getPending } = usePendingChanges();
  const pending = getPending(entityType, entityId, field);
  return [pending ? (pending.newValue as T) : serverValue, !!pending];
}
